import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving safe HTML formatting.
 * Uses DOMPurify to remove potentially malicious content.
 *
 * @param html - The HTML string to sanitize
 * @returns Sanitized HTML string safe for rendering with dangerouslySetInnerHTML
 */
export const sanitizeHtml = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'b',
      'em',
      'i',
      'u',
      'ul',
      'ol',
      'li',
      'a',
      'span',
      'div',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'blockquote',
      'code',
      'pre',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
      'img',
      'hr',
      'small',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'alt', 'title', 'src'],
    ALLOWED_URI_REGEXP: /^(?:https?|mailto|ftp):/i,
    ALLOW_DATA_ATTR: false,
  });
};

/**
 * Validates that a URL is a safe data: URL (typically base64 encoded images).
 * Returns the URL if valid, or an empty string if potentially malicious.
 * Note: SVG is intentionally excluded as it can contain embedded scripts.
 *
 * @param url - The data URL to validate
 * @returns The original URL if valid, empty string otherwise
 */
export const validateDataUrl = (url: string | null | undefined): string => {
  if (!url) {
    return '';
  }

  // Only allow data: URLs that start with safe image MIME types (excluding SVG)
  const safeDataUrlPattern = /^data:image\/(png|jpeg|jpg|gif|webp);base64,/i;

  if (safeDataUrlPattern.test(url)) {
    return url;
  }

  // Reject any other URLs including javascript:, data:text/html, data:image/svg+xml, etc.
  return '';
};

/**
 * Escapes HTML special characters to prevent XSS when inserting text into HTML contexts.
 *
 * @param text - The string to escape
 * @returns HTML-escaped string
 */
export const escapeHtml = (text: string): string => {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

/**
 * Sanitizes an SVG string to remove potentially malicious content.
 * Strips script tags, event handlers, and foreignObject elements.
 *
 * @param svg - The SVG string to sanitize
 * @returns Sanitized SVG string safe for rendering
 */
export const sanitizeSvg = (svg: string): string => {
  if (!svg) return '';
  return DOMPurify.sanitize(svg, {
    USE_PROFILES: { svg: true, svgFilters: true },
    FORBID_TAGS: ['script', 'foreignObject'],
    FORBID_ATTR: ['onload', 'onerror', 'onclick', 'onmouseover'],
  });
};
